import { ChevronDown, ChevronUp } from "lucide-react";
import gibletOwl from "@/assets/giblet-owl.png";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

interface GibletGuideProps {
  stage: 'inform' | 'decide' | 'execute' | 'assess';
  milestone?: string;
}

const stageTips = {
  inform: {
    title: "Inform Stage",
    tip: "Upload files, link knowledge bases, or paste content to inform your sprint objective. The more context you provide, the better AI decisions you'll get!",
    bestPractices: [
      "Include diverse perspectives and sources",
      "Upload relevant documents or research",
      "Link to existing knowledge bases from your profile",
      "Provide context about constraints or requirements"
    ]
  },
  decide: {
    title: "Decide Stage",
    tip: "Vote on the best strategic approach based on your expertise. AI has analyzed your content and generated options - choose wisely!",
    bestPractices: [
      "Read the full rationale before voting",
      "Consider AI confidence scores as a guide",
      "Vote for approaches that best fit your objective",
      "You can change your vote before completing the stage"
    ]
  },
  execute: {
    title: "Execute Stage",
    tip: "Craft your prompt using the decisions made. Iterate as needed until you get the perfect output!",
    bestPractices: [
      "Start with the auto-generated prompt",
      "Be specific about desired output format",
      "Iterate multiple times to refine results",
      "Save your best iterations for comparison"
    ]
  },
  assess: {
    title: "Assess Stage",
    tip: "Rate your peers' work honestly - it shapes their GibRating. Quality feedback helps everyone improve!",
    bestPractices: [
      "Consider both creativity and execution",
      "Provide constructive feedback",
      "Be specific in your comments",
      "Remember: your ratings impact real GibRatings"
    ]
  }
};

export const GibletGuide = ({ stage, milestone }: GibletGuideProps) => {
  const [expanded, setExpanded] = useState(false);
  const guide = stageTips[stage];

  return (
    <Card className="bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20">
      <div className="p-4">
        <div className="flex items-start gap-3">
          <motion.div
            animate={{ 
              rotate: [0, 10, -10, 0],
              scale: [1, 1.1, 1]
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              repeatDelay: 3
            }}
            className="w-8 h-8 flex-shrink-0 mt-1"
          >
            <img src={gibletOwl} alt="Giblet" className="w-full h-full object-contain" />
          </motion.div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-2">
              <h4 className="font-semibold text-sm">Giblet's Guide: {guide.title}</h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExpanded(!expanded)}
                className="h-6 w-6 p-0"
              >
                {expanded ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </Button>
            </div>
            
            <p className="text-sm text-muted-foreground mb-2">
              {guide.tip}
            </p>

            <AnimatePresence>
              {milestone && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="text-sm font-medium text-primary mb-2"
                >
                  {milestone}
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {expanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="pt-3 border-t border-primary/20 mt-2">
                    <p className="text-sm font-medium mb-2">Best Practices:</p>
                    <ul className="space-y-1">
                      {guide.bestPractices.map((practice, i) => (
                        <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                          <span className="text-primary mt-1">•</span>
                          <span>{practice}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </Card>
  );
};
